import { useEffect, useRef, useState } from "react";
import i18n from "../../../language/i18n";
import { perfChartsStyles } from "./PerfChartsStyles";

// Bespoke SVG performance charts — faithful ports of the design ReportsScreen
// ScoreRings / GoalRing / ScoreLanes. All copy/labels arrive via props so the
// caller (ReportCardView) supplies i18n. Styling: rp- (PerfChartsStyles).

// Design chart palette (student / branch / class).
export const C_STUDENT = "#73D7E6";
export const C_BRANCH = "#0F899C";
export const C_CLASS = "#114859";

// Per-SOURCE hues for the gelişim (improvement over time) chart, where several assessment sources
// share one axis and the colour is what tells them apart. Distinct from the three above, which
// encode WHOSE score a line is (learner / şube / sınıf) within a single source.
export const C_PERSONALIZED = "#B07CD8";
export const C_HANDWRITTEN = "#E8A33D";
// Deneme sınavı (mock exam). Same hue the staff console uses for this source, so a teacher
// and a learner reading the same exam see the same colour.
export const C_MOCK_EXAM = "#3FA796";
// Destek ödevi — the warm orange the staff console's kind chip gives this kind.
export const C_SUPPORT = "#B0561C";

interface RingDatum {
  key: string;
  label: string;
  short: string;
  value: number | null;
  color: string;
}

// useReveal — entrance trigger (rAF + IntersectionObserver + timeout fallback).
const useReveal = () => {
  const [lit, setLit] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setLit(true)),
    );
    const el = ref.current;
    let io: IntersectionObserver | undefined;
    if (el && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (es) => es.forEach((e) => e.isIntersecting && setLit(true)),
        { threshold: 0.2 },
      );
      io.observe(el);
    }
    const fb = setTimeout(() => setLit(true), 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fb);
      if (io) io.disconnect();
    };
  }, []);
  return { lit, ref };
};

// ScoreRings — concentric radial gauges, one ring per group (0–100).
export function ScoreRings({
  rings,
  emptyText,
}: {
  rings: RingDatum[];
  emptyText: string;
}) {
  const { lit, ref } = useReveal();
  const size = 184;
  const cx = size / 2;
  const cy = size / 2;
  const sw = 15;
  const gap = 6;
  const data = rings.filter((r) => r.value != null);
  const empty = data.length === 0;
  const hero = data.find((r) => r.key === "student") || data[0];
  return (
    <div className="rp-rings" ref={ref}>
      {empty ? (
        <div className="rp-rings__empty">
          <div className="rp-rings__emptydot">—</div>
          <span>{emptyText}</span>
        </div>
      ) : (
        <>
          <div className="rp-rings__chart">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {data.map((r, i) => {
                const rad = size / 2 - sw / 2 - i * (sw + gap);
                const circ = 2 * Math.PI * rad;
                const len = ((lit ? (r.value as number) : 0) / 100) * circ;
                return (
                  <g key={r.key}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={rad}
                      fill="none"
                      stroke="var(--catchup-gray-100)"
                      strokeWidth={sw}
                    />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={rad}
                      fill="none"
                      stroke={r.color}
                      strokeWidth={sw}
                      strokeLinecap="round"
                      strokeDasharray={circ}
                      strokeDashoffset={circ - len}
                      transform={`rotate(-90 ${cx} ${cy})`}
                      style={{
                        transition:
                          "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)",
                      }}
                    />
                  </g>
                );
              })}
            </svg>
            {hero ? (
              <div className="rp-rings__center">
                <span className="rp-rings__num">
                  {Math.round(hero.value as number)}
                </span>
                <span className="rp-rings__lbl">{hero.short}</span>
              </div>
            ) : null}
          </div>
          <div className="rp-rings__legend">
            {data.map((r) => (
              <div className="rp-rings__row" key={r.key}>
                <span
                  className="rp-rings__dot"
                  style={{ background: r.color }}
                ></span>
                <span className="rp-rings__name">{r.label}</span>
                <span className="rp-rings__val" style={{ color: r.color }}>
                  {Math.round(r.value as number)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// GoalRing — captured-vs-goal gauge: full blue track + coral progress arc.
export function GoalRing({
  value,
  goal,
  label,
  unit,
  subLabel,
  valueText,
}: {
  value: number;
  goal: number;
  label: string;
  unit: string;
  subLabel: string;
  /** Pre-formatted centre number (e.g. "381,6"); default is `value` to one decimal. */
  valueText?: string;
}) {
  const { lit, ref } = useReveal();
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const swOuter = 16;
  const swInner = 14;
  const ringGap = 7;
  const rOuter = size / 2 - swOuter / 2 - 3;
  const rInner = rOuter - swOuter / 2 - ringGap - swInner / 2;
  const circI = 2 * Math.PI * rInner;
  const pct = Math.max(0, Math.min(100, (value / goal) * 100));
  const dashI = circI * (pct / 100);
  const fmt = (n: number) =>
    (Math.round(n * 10) / 10).toString().replace(/\.0$/, "");
  return (
    <div className="rp-goal" ref={ref}>
      <div className="rp-goal__cap">{label}</div>
      <div className="rp-goal__main">
        <div className="rp-goal__chart">
          <svg
            className="rp-goal__svg"
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
          >
            <circle
              cx={cx}
              cy={cy}
              r={rOuter}
              fill="none"
              stroke="var(--catchup-blue-500)"
              strokeWidth={swOuter}
            />
            <circle
              cx={cx}
              cy={cy}
              r={rInner}
              fill="none"
              stroke="rgba(242,134,95,0.16)"
              strokeWidth={swInner}
            />
            <circle
              cx={cx}
              cy={cy}
              r={rInner}
              fill="none"
              stroke="var(--accent-coral)"
              strokeWidth={swInner}
              strokeLinecap="round"
              strokeDasharray={circI}
              strokeDashoffset={lit ? circI - dashI : circI}
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{
                transition: "stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)",
              }}
            />
          </svg>
          <div className="rp-goal__disc">
            <span className="rp-goal__num">
              {unit}
              {valueText ?? fmt(value)}
            </span>
            <span className="rp-goal__sub">{subLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The rail's value range. Default 0–100 (a percentage) — what every existing caller draws. A deneme's
 * Tahmini puan sits on 100–500, so the deneme list passes its own range, ticks and number format.
 */
export interface LaneScale {
  min: number;
  max: number;
  ticks: number[];
  format?: (value: number) => string;
}
const PERCENT_SCALE: LaneScale = { min: 0, max: 100, ticks: [0, 25, 50, 75, 100] };

interface LaneGroup {
  label: string;
  values: Record<string, number | null | undefined>;
}
interface LaneSeries {
  key: string;
  label: string;
  color: string;
}

// ScoreLanes — one rail per assignment: student = filled bar, branch/class =
// diamond markers on the same rail; legend markers are show/hide toggles.
export function ScoreLanes({
  title,
  groups,
  series,
  emptyText,
  scale = PERCENT_SCALE,
}: {
  title: string;
  groups: LaneGroup[];
  series: LaneSeries[];
  emptyText: string;
  scale?: LaneScale;
}) {
  const { lit, ref } = useReveal();
  const span = scale.max - scale.min || 1;
  // Value → position on the rail (0–100 %), clamped so an out-of-range value can't draw past the rail.
  const toPct = (value: number) => Math.max(0, Math.min(100, ((value - scale.min) / span) * 100));
  const fmtValue = scale.format ?? ((value: number) => String(Math.round(value)));
  const innerTicks = scale.ticks.slice(1, -1);
  const studentS = series.find((s) => s.key === "student");
  const markerSeries = series.filter((s) => s.key !== "student");
  const [shown, setShown] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    markerSeries.forEach((s, i) => {
      init[s.key] = i === 0;
    });
    return init;
  });
  const toggle = (k: string) => setShown((s) => ({ ...s, [k]: !s[k] }));
  return (
    <div className="rp-lanes" ref={ref}>
      <div className="rp-lanes__head">
        {title ? <h3 className="rp-chart__title">{title}</h3> : null}
        <div className="rp-legend">
          {studentS ? (
            <span className="rp-legend__item">
              <i
                className="rp-lg rp-lg--you"
                style={{ background: studentS.color }}
              ></i>
              {studentS.label}
            </span>
          ) : null}
          {markerSeries.map((s) => (
            <button
              key={s.key}
              type="button"
              className={"rp-legtog" + (shown[s.key] ? " is-on" : "")}
              onClick={() => toggle(s.key)}
            >
              <i
                className="rp-lg"
                style={{
                  background: shown[s.key]
                    ? s.color
                    : "var(--catchup-gray-300)",
                }}
              ></i>
              {s.label}
              <span className="rp-legtog__hint">
                {shown[s.key] ? "✓" : i18n.t("show")}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="rp-lanes__scale" aria-hidden="true">
        {scale.ticks.map((t) => (
          <span key={t} className="rp-lanes__tick">
            <b>{t}</b>
          </span>
        ))}
      </div>

      <div className="rp-lanes__list">
        {groups.length === 0 ? (
          <div className="rp-lane">
            <div className="rp-lane__empty">{emptyText}</div>
          </div>
        ) : (
          groups.map((g, gi) => {
            const sv = studentS ? g.values[studentS.key] : null;
            const markers = markerSeries
              .filter((s) => shown[s.key] && g.values[s.key] != null)
              .map((s) => ({
                key: s.key,
                color: s.color,
                label: s.label,
                val: g.values[s.key] as number,
              }));
            const isEmpty = sv == null && markers.length === 0;
            return (
              <div className="rp-lane" key={gi}>
                <div className="rp-lane__name" title={g.label}>
                  {g.label}
                </div>
                <div className="rp-lane__track">
                  {isEmpty ? (
                    <div className="rp-lane__empty">{emptyText}</div>
                  ) : (
                    <div className="rp-lane__rail">
                      {innerTicks.map((t) => (
                        <span
                          key={t}
                          className="rp-lane__grid"
                          style={{ left: toPct(t) + "%" }}
                        ></span>
                      ))}
                      {sv != null ? (
                        <div
                          className="rp-lane__fill"
                          style={{ width: (lit ? toPct(sv) : 0) + "%" }}
                        ></div>
                      ) : null}
                      {sv != null ? (
                        <span
                          className="rp-lane__cap"
                          style={{ left: (lit ? toPct(sv) : 0) + "%" }}
                        >
                          {fmtValue(sv)}
                        </span>
                      ) : null}
                      {markers.map((m, mi) => (
                        <span
                          key={m.key}
                          className={"rp-mark" + (mi % 2 ? " rp-mark--alt" : "")}
                          style={{ left: (lit ? toPct(m.val) : 0) + "%" }}
                        >
                          <span
                            className="rp-mark__diamond"
                            style={{ background: m.color }}
                          ></span>
                          <span
                            className="rp-mark__lbl"
                            style={{ color: m.color }}
                          >
                            {m.label} {fmtValue(m.val)}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// PerfOverview — the design's rp-perf composition: an "overall scores" glass
// card (ScoreRings, or GoalRing when only the student score exists) beside a
// ScoreLanes chart card. The results table is rendered separately by the caller.
export function PerfOverview({
  overallTitle,
  rings,
  goalRing,
  chartTitle,
  groups,
  series,
  laneEmptyText,
  ringEmptyText,
  laneScale,
}: {
  overallTitle: string;
  rings: RingDatum[];
  goalRing: {
    value: number;
    goal: number;
    label: string;
    unit: string;
    subLabel: string;
    valueText?: string;
  } | null;
  chartTitle: string;
  groups: LaneGroup[];
  series: LaneSeries[];
  laneEmptyText: string;
  ringEmptyText: string;
  /** Rail range for the lanes; default 0–100. */
  laneScale?: LaneScale;
}) {
  return (
    <>
      <style>{perfChartsStyles}</style>
      <div className="rp-perf">
        <div className="rp-overall">
          <h3 className="rp-overall__title">{overallTitle}</h3>
          {goalRing ? (
            <GoalRing {...goalRing} />
          ) : (
            <ScoreRings rings={rings} emptyText={ringEmptyText} />
          )}
        </div>
        <div className="rp-chartwrap">
          <ScoreLanes
            title={chartTitle}
            groups={groups}
            series={series}
            emptyText={laneEmptyText}
            scale={laneScale}
          />
        </div>
      </div>
    </>
  );
}
